import {
  createContext,
  FC,
  PropsWithChildren,
  Reducer,
  useCallback,
  useContext,
  useReducer,
  useRef,
} from 'react';

export interface WebsocketState {
  websocket: WebSocket | null;
}

export interface WebsocketAction {
  type: WebsocketActionType;
  websocket: WebSocket;
}

export interface WebsocketMessageOptions {
  skipLastMessage?: boolean;
  resetLastMessage?: boolean;
}

export interface WebsocketContextValue extends WebsocketState {
  connectWebsocket: (url: string) => void;
  sendMessage: (data: string, options?: WebsocketMessageOptions) => void;
  closeWebsocket: () => void;
  subscribeToMessages: (subscribers: (data: any, lastMessage: any) => void) => void;
}

enum WebsocketActionType {
  OPEN_WEB_SOCKET = 'openWebsocket',
  CLOSE_WEB_SOCKET = 'closeWebsocket',
}

const initialState: WebsocketState = {
  websocket: null,
};

const websocketReducer: Reducer<WebsocketState, WebsocketAction> = (state, action) => {
  switch (action.type) {
    case WebsocketActionType.OPEN_WEB_SOCKET:
      return {
        ...state,
        websocket: action.websocket,
      };

    case WebsocketActionType.CLOSE_WEB_SOCKET:
      return {
        ...state,
        websocket: null,
      };

    default:
      return state;
  }
};

export const WebsocketContext = createContext<WebsocketContextValue>({
  ...initialState,
} as WebsocketContextValue);

export const useWebsocket = () => useContext(WebsocketContext);

export const WebsocketProvider: FC<PropsWithChildren> = ({ children }) => {
  const [state, dispatch] = useReducer(websocketReducer, initialState);

  const wsRef = useRef<WebSocket>();
  const lastMessage = useRef<string>();
  const subscribersRef = useRef<
    Array<(data: string, lastMessage: React.MutableRefObject<string | undefined>) => void>
  >([]);

  const reconnectRef = useRef<NodeJS.Timeout>();
  const heartbeatRef = useRef<NodeJS.Timeout>();

  const closeWebsocket = useCallback(() => {
    wsRef.current?.close();
    clearTimeout(reconnectRef.current);
    clearTimeout(heartbeatRef.current);
  }, []);

  const sendMessage = useCallback((data: string, options?: WebsocketMessageOptions) => {
    if (wsRef.current?.readyState === 1) {
      wsRef.current?.send?.(data);
    }

    if (options?.resetLastMessage) {
      lastMessage.current = undefined;
    } else if (!options?.skipLastMessage) {
      lastMessage.current = data;
    }
  }, []);

  const heartbeat = useCallback(() => {
    sendMessage(JSON.stringify({ operation: 'ack', message: 'heartbeat' }), {
      skipLastMessage: true,
    });

    if (!!heartbeatRef.current) {
      clearTimeout(heartbeatRef.current);
    }

    // Retry in 5 seconds
    heartbeatRef.current = setTimeout(heartbeat, 5000);
  }, [sendMessage]);

  const connectWebsocket = useCallback(
    (url: string) => {
      wsRef.current = new WebSocket(url);
      wsRef.current.binaryType = 'arraybuffer';

      wsRef.current.onmessage = (e) => {
        const data = JSON.parse(e.data);
        subscribersRef.current.forEach((s) => s(data, lastMessage));
      };

      wsRef.current.onclose = (e) => {
        if (e.wasClean) {
          return;
        }

        if (!!reconnectRef.current) {
          clearTimeout(reconnectRef.current);
        }

        // Retry in 5 seconds
        reconnectRef.current = setTimeout(() => {
          connectWebsocket(url);

          // retry last search
          setTimeout(() => {
            if (lastMessage.current) {
              sendMessage(lastMessage.current);
            }
          }, 500);
        }, 5000);
      };

      heartbeat();

      dispatch({
        type: WebsocketActionType.OPEN_WEB_SOCKET,
        websocket: wsRef.current,
      });
    },
    [heartbeat, sendMessage],
  );

  const subscribeToMessages = useCallback((subscriber: (data: any, lastMessage: any) => void) => {
    subscribersRef.current.push(subscriber);
  }, []);

  return (
    <WebsocketContext.Provider
      value={{
        ...state,
        closeWebsocket,
        connectWebsocket,
        sendMessage,
        subscribeToMessages,
      }}
    >
      {children}
    </WebsocketContext.Provider>
  );
};
